import React, { useState } from 'react';
import { Project } from '../../types';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';
import { 
    Sparkles, Copy, Check, Eye, EyeOff, Terminal, 
    Send, Info, Lock, Key, ClipboardList, CheckCircle2,
    Code, Server, Settings, ChevronRight, FolderKanban,
    Paperclip, X
} from 'lucide-react';

interface AiApiPageProps {
    projects: Project[];
    currentUser: any;
}

export const AiApiPage: React.FC<AiApiPageProps> = ({ projects, currentUser }) => {
    const [selectedProjectId, setSelectedProjectId] = useState(projects[0]?.id || '');
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedIssue, setGeneratedIssue] = useState<any>(null);
    const [copiedProjectId, setCopiedProjectId] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showToken, setShowToken] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);
    const [curlCopied, setCurlCopied] = useState(false);

    const token = localStorage.getItem('planner_token') || '';

    const handleCopyProjectId = (projectId: string) => {
        navigator.clipboard.writeText(projectId);
        setCopiedProjectId(projectId);
        toast.success("Project ID copied to clipboard!");
        setTimeout(() => setCopiedProjectId(null), 2000);
    };

    const handleCopyToken = () => {
        if (!token) return;
        navigator.clipboard.writeText(token);
        setTokenCopied(true);
        toast.success("Access Token copied to clipboard!");
        setTimeout(() => setTokenCopied(false), 2000);
    };

    const curlCommand = `# 1. Create with AI prompt (JSON body)
curl -X POST "https://planner.adlift.com/api/issues/ai-create" \\
  -H "Authorization: Bearer ${token ? token.substring(0, 15) + '...' : '<YOUR_ACCESS_TOKEN>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": "${selectedProjectId || '<PROJECT_ID>'}",
    "prompt": "Create a high priority bug: Login page throws 505 error, assign to Mohit, estimate 4 hours, due next Friday"
  }'

# 2. Create with AI prompt + attachments (multipart/form-data)
curl -X POST "https://planner.adlift.com/api/issues/ai-create" \\
  -H "Authorization: Bearer ${token ? token.substring(0, 15) + '...' : '<YOUR_ACCESS_TOKEN>'}" \\
  -F "payload={\\"project_id\\": \\"${selectedProjectId || '<PROJECT_ID>'}\\", \\"prompt\\": \\"Create a high priority bug: Login page throws 505 error, assign to Mohit, estimate 4 hours, due next Friday\\"}" \\
  -F "files=@/path/to/screenshot.png"`;

    const handleCopyCurl = () => {
        const fullCurlCommand = `# 1. Create with AI prompt (JSON body)
curl -X POST "https://planner.adlift.com/api/issues/ai-create" \\
  -H "Authorization: Bearer ${token || '<YOUR_ACCESS_TOKEN>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "project_id": "${selectedProjectId || '<PROJECT_ID>'}",
    "prompt": "Create a high priority bug: Login page throws 505 error, assign to Mohit, estimate 4 hours, due next Friday"
  }'

# 2. Create with AI prompt + attachments (multipart/form-data)
curl -X POST "https://planner.adlift.com/api/issues/ai-create" \\
  -H "Authorization: Bearer ${token || '<YOUR_ACCESS_TOKEN>'}" \\
  -F "payload={\\"project_id\\": \\"${selectedProjectId || '<PROJECT_ID>'}\\", \\"prompt\\": \\"Create a high priority bug: Login page throws 505 error, assign to Mohit, estimate 4 hours, due next Friday\\"}" \\
  -F "files=@/path/to/screenshot.png"`;
        navigator.clipboard.writeText(fullCurlCommand);
        setCurlCopied(true);
        toast.success("Curl commands copied to clipboard!");
        setTimeout(() => setCurlCopied(false), 2000);
    };

    const handleTestAiApi = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedProjectId) {
            toast.error("Please select a project first.");
            return;
        }
        if (!prompt.trim()) {
            toast.error("Please enter a prompt.");
            return;
        }

        setIsGenerating(true);
        setGeneratedIssue(null);
        try {
            const result = await apiService.createIssueWithAi(selectedProjectId, prompt, selectedFiles);
            setGeneratedIssue(result);
            toast.success(`Issue ${result.key} created successfully!`);
            setPrompt('');
            setSelectedFiles([]);
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || "Failed to create issue via AI API");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="flex-1 overflow-auto bg-surface-50 p-6">
            {/* Header section */}
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-6 h-6 text-brand-600 animate-pulse" />
                        <h1 className="text-2xl font-bold text-surface-900">AI API Issue Integration</h1>
                    </div>
                    <p className="text-surface-500">
                        Use the planner AI API to programmatically generate rich, detailed issues from plain text descriptions.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                {/* Left Column: API Documentation & Token */}
                <div className="xl:col-span-7 space-y-6">
                    {/* Access Token Card */}
                    <div className="bordio-card p-6 border-brand-100 bg-white shadow-soft">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600">
                                <Key className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-surface-900">Your Developer Token</h3>
                                <p className="text-xs text-surface-400">Authenticate your API requests using this Bearer token.</p>
                            </div>
                        </div>

                        <div className="relative mt-2">
                            <div className="flex items-center gap-2 p-3 bg-surface-50 rounded-lg border border-surface-200">
                                <Lock className="w-4 h-4 text-surface-400 flex-shrink-0" />
                                <div className="flex-1 overflow-hidden">
                                    <p className="font-mono text-xs text-surface-700 truncate select-all select-none">
                                        {showToken ? token : '•'.repeat(Math.min(token.length || 32, 64))}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setShowToken(!showToken)}
                                        className="p-1.5 hover:bg-surface-200 rounded text-surface-500 transition-colors"
                                        title={showToken ? "Hide token" : "Show token"}
                                    >
                                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCopyToken}
                                        className="p-1.5 hover:bg-surface-200 rounded text-surface-500 transition-colors relative"
                                        title="Copy token"
                                    >
                                        {tokenCopied ? (
                                            <Check className="w-4 h-4 text-accent-green" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 flex items-start gap-2 bg-blue-50/50 border border-blue-100 p-3 rounded-lg text-xs text-blue-800">
                            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p>
                                This developer token is unique to your account. You can only create issues in projects where you are a member, and other users cannot access or view your token. Keep it secure.
                            </p>
                        </div>
                    </div>

                    {/* Your Projects Card */}
                    <div className="bordio-card p-6 bg-white shadow-soft">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-accent-green">
                                <FolderKanban className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-surface-900">Your Projects</h3>
                                <p className="text-xs text-surface-400">Copy the Project ID to use in your API requests.</p>
                            </div>
                        </div>

                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                            {projects.length === 0 ? (
                                <p className="text-xs text-surface-400 text-center py-4">No projects found.</p>
                            ) : (
                                projects.map((project) => (
                                    <div key={project.id} className="flex items-center justify-between p-3 bg-surface-50 rounded-lg border border-surface-200 hover:border-surface-300 transition-colors">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-6 h-6 bg-brand-100 text-brand-600 rounded flex items-center justify-center font-bold text-2xs flex-shrink-0">
                                                {project.key?.charAt(0) || project.name.charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <span className="text-xs font-semibold text-surface-800 block truncate">{project.name}</span>
                                                <span className="text-2xs text-surface-400 font-mono">{project.key}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                            <span className="font-mono text-2xs text-surface-500 bg-surface-150 px-2 py-1 rounded hidden sm:inline truncate max-w-[150px]" title={project.id}>
                                                {project.id}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyProjectId(project.id)}
                                                className="p-1 hover:bg-surface-200 rounded text-surface-500 transition-colors"
                                                title="Copy Project ID"
                                            >
                                                {copiedProjectId === project.id ? (
                                                    <Check className="w-3.5 h-3.5 text-accent-green" />
                                                ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* API Details Card */}
                    <div className="bordio-card p-6 bg-white shadow-soft">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                                <Terminal className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-surface-900">API Endpoint</h3>
                                <p className="text-xs text-surface-400">Endpoint details, parameters reference and schema format.</p>
                            </div>
                        </div>

                        {/* Endpoint Route Info */}
                        <div className="flex items-center gap-3 p-3 bg-surface-900 rounded-lg text-white mb-6">
                            <span className="bg-orange-600 text-xs font-bold px-2 py-1 rounded">POST</span>
                            <span className="font-mono text-xs tracking-wider flex-1 truncate">/api/issues/ai-create</span>
                            <span className="text-2xs text-surface-400 font-mono hidden sm:inline">JSON Payload</span>
                        </div>

                        {/* Schema parameters */}
                        <div className="space-y-6">
                            <div>
                                <h4 className="text-xs font-bold text-surface-500 uppercase tracking-wide mb-3">
                                    Request Body Parameters
                                </h4>
                                <div className="border border-surface-200 rounded-lg overflow-hidden divide-y divide-surface-200">
                                    <div className="p-3 bg-surface-50 grid grid-cols-12 gap-2 text-xs font-semibold text-surface-700">
                                        <div className="col-span-3">Field</div>
                                        <div className="col-span-2">Type</div>
                                        <div className="col-span-2">Requirement</div>
                                        <div className="col-span-5">Description</div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono font-semibold text-brand-600">project_id</div>
                                        <div className="col-span-2">
                                            <span className="font-mono text-2xs bg-surface-100 px-1.5 py-0.5 rounded text-surface-600">string (UUID)</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-2xs bg-red-50 text-accent-red font-semibold px-2 py-0.5 rounded border border-red-100">Required</span>
                                        </div>
                                        <div className="col-span-5 text-surface-600">
                                            The unique ID of the target project. Copy it from the "Your Projects" section above.
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono font-semibold text-brand-600">prompt</div>
                                        <div className="col-span-2">
                                            <span className="font-mono text-2xs bg-surface-100 px-1.5 py-0.5 rounded text-surface-600">string</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-2xs bg-red-50 text-accent-red font-semibold px-2 py-0.5 rounded border border-red-100">Required</span>
                                        </div>
                                        <div className="col-span-5 text-surface-600">
                                            The natural language instruction describing the issue. The AI model will parse this prompt to populate individual ticket fields automatically.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-surface-500 uppercase tracking-wide mb-3">
                                    AI-Extracted Fields (Guided by Prompt Text)
                                </h4>
                                <div className="border border-surface-200 rounded-lg overflow-hidden divide-y divide-surface-200">
                                    <div className="p-3 bg-surface-50 grid grid-cols-12 gap-2 text-xs font-semibold text-surface-700">
                                        <div className="col-span-3">Extracted Field</div>
                                        <div className="col-span-3">Allowed / Expected Format</div>
                                        <div className="col-span-6">Prompt Example Instruction</div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Type</div>
                                        <div className="col-span-3 text-surface-500 font-mono text-2xs">bug | task | story | epic | subtask</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Create a <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">bug</span>: Login page has alignment issues"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Priority</div>
                                        <div className="col-span-3 text-surface-500 font-mono text-2xs">lowest | low | medium | high | highest</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Set priority to <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">highest</span> for production backup error"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Assignee</div>
                                        <div className="col-span-3 text-surface-500 text-2xs">Any project member's Name/Email</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Create task X and <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">assign it to Mohit</span>"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Story Points</div>
                                        <div className="col-span-3 text-surface-500 text-2xs">Integer numbers</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Add story points of <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">5 points</span> to this story"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Labels</div>
                                        <div className="col-span-3 text-surface-500 text-2xs">List of comma-separated tags</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Create task Y <span className="font-semibold text-surface-800 bg-surface-100 bg-surface-100 px-1 rounded not-italic">labeled frontend, UI</span>"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Due Date / Deadline</div>
                                        <div className="col-span-3 text-surface-500 text-2xs">Date / relative timeframe</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Fix the backup script <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">by next Monday</span>" or "<span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">due 2026-06-25</span>"
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-12 gap-2 text-xs bg-white">
                                        <div className="col-span-3 font-mono text-surface-750 font-semibold">Estimation Time (Hours)</div>
                                        <div className="col-span-3 text-surface-500 text-2xs">Integer numbers (hours)</div>
                                        <div className="col-span-6 text-surface-600 italic">
                                            "Design the login flow, <span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">estimate 6 hours</span>" or "<span className="font-semibold text-surface-800 bg-surface-100 px-1 rounded not-italic">should take 4 hours</span>"
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Curl Example */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-bold text-surface-500 uppercase tracking-wide">CURL Command Example</h4>
                                <button
                                    onClick={handleCopyCurl}
                                    className="flex items-center gap-1 text-2xs text-brand-600 hover:text-brand-700 font-medium"
                                >
                                    {curlCopied ? (
                                        <>
                                            <Check className="w-3.5 h-3.5 text-accent-green" />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3.5 h-3.5" />
                                            Copy Code
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="p-4 bg-surface-50 rounded-lg overflow-x-auto border border-surface-200">
                                <pre className="font-mono text-xs text-black whitespace-pre">
                                    {curlCommand}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Prompt Tester Widget */}
                <div className="xl:col-span-5 space-y-6">
                    <div className="bordio-card p-6 bg-white shadow-soft border border-surface-100">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-surface-900">Try Interactive Tester</h3>
                                <p className="text-xs text-surface-400">Submit an AI prompt here to test generating an issue live.</p>
                            </div>
                        </div>

                        <form onSubmit={handleTestAiApi} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1.5">
                                    Target Project
                                </label>
                                <select
                                    value={selectedProjectId}
                                    onChange={(e) => setSelectedProjectId(e.target.value)}
                                    className="w-full text-sm p-2 bg-white rounded-lg border border-surface-300 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                                >
                                    {projects.map((project) => (
                                        <option key={project.id} value={project.id}>
                                            {project.name} ({project.key})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1.5">
                                    Issue Description Prompt
                                </label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="Describe the issue. E.g. 'Create a bug with high priority: checkout button not working on mobile Safari. Assign to Mohit.'"
                                    rows={4}
                                    className="w-full text-sm p-3 bg-white rounded-lg border border-surface-300 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 placeholder-surface-300 resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1.5 flex items-center gap-1.5">
                                    <Paperclip className="w-3.5 h-3.5 text-surface-400" />
                                    Attachments (Optional)
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-surface-200 border-dashed rounded-lg hover:border-brand-300 hover:bg-surface-50/20 transition-all cursor-pointer relative">
                                    <input
                                        type="file"
                                        multiple
                                        onChange={(e) => {
                                            if (e.target.files) {
                                                setSelectedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                                            }
                                        }}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                    <div className="space-y-1 text-center pointer-events-none">
                                        <Paperclip className="mx-auto h-8 w-8 text-surface-300 animate-bounce" />
                                        <div className="flex text-xs text-surface-500 justify-center">
                                            <span className="text-brand-600 font-semibold hover:text-brand-500">Upload files</span>
                                            <p className="pl-1">or drag and drop</p>
                                        </div>
                                        <p className="text-2xs text-surface-400">Max size 10MB per file</p>
                                    </div>
                                </div>

                                {selectedFiles.length > 0 && (
                                    <div className="mt-3 space-y-1.5">
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="flex items-center justify-between p-2 bg-surface-50 border border-surface-200 rounded-lg text-xs">
                                                <div className="flex items-center gap-2 truncate">
                                                    <Paperclip className="w-3.5 h-3.5 text-surface-400 flex-shrink-0" />
                                                    <span className="truncate font-medium text-surface-700">{file.name}</span>
                                                    <span className="text-2xs text-surface-400">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                                    className="p-1 hover:bg-surface-200 rounded text-surface-400 hover:text-accent-red transition-colors"
                                                    title="Remove file"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isGenerating || !selectedProjectId}
                                className="w-full btn-primary py-2.5 flex items-center justify-center gap-2 text-white font-medium bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm disabled:opacity-55 disabled:cursor-not-allowed transition-colors"
                            >
                                {isGenerating ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Generating via AI...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Create Ticket with AI
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Prompt Suggestions */}
                        <div className="mt-5 pt-4 border-t border-surface-100">
                            <span className="text-2xs font-semibold text-surface-400 uppercase tracking-wider block mb-2">
                                Try prompt suggestions:
                            </span>
                            <div className="space-y-2">
                                {[
                                    "Create a bug with highest priority: sidebar menu option is overlapping. Estimate 4 hours, due next Monday, assign to Mohit",
                                    "Create a story with 5 story points: User profile page should show timezone dropdown selection. Estimate 8 hours",
                                    "Create a task: Add unit tests for the oauth middleware integration. Due by Friday, estimate 3 hours"
                                ].map((suggestion, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setPrompt(suggestion)}
                                        className="w-full text-left text-xs text-brand-600 hover:text-brand-700 hover:bg-brand-50/50 p-2 rounded border border-dashed border-surface-200 flex items-center gap-1.5 transition-colors"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="truncate">{suggestion}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Result Card */}
                    {generatedIssue && (
                        <div className="bordio-card p-6 bg-emerald-50/20 border-emerald-100 rounded-xl animate-fade-in">
                            <div className="flex items-center gap-2 text-accent-green mb-4">
                                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                                <h4 className="text-sm font-semibold">Issue Created Successfully</h4>
                            </div>

                            <div className="bg-white border border-surface-150 p-4 rounded-lg space-y-3 shadow-2xs">
                                <div className="flex items-center justify-between border-b border-surface-100 pb-2">
                                    <span className="text-xs font-bold text-surface-900 bg-brand-50 px-2 py-0.5 rounded">
                                        {generatedIssue.key}
                                    </span>
                                    <span className="text-2xs text-surface-400 capitalize">
                                        {generatedIssue.type}
                                    </span>
                                </div>

                                <div>
                                    <h5 className="text-sm font-semibold text-surface-900">{generatedIssue.summary}</h5>
                                    {generatedIssue.description && (
                                        <p className="text-xs text-surface-500 mt-1 line-clamp-3">
                                            {generatedIssue.description}
                                        </p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-2 pt-2 text-2xs border-t border-surface-50 text-surface-500">
                                    <div>
                                        <span className="font-semibold block text-surface-400">Priority:</span>
                                        <span className="capitalize">{generatedIssue.priority}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-surface-400">Assignee:</span>
                                        <span>{generatedIssue.assignee_user?.display_name || 'Unassigned'}</span>
                                    </div>
                                    {(generatedIssue.due_date || generatedIssue.dueDate) && (
                                        <div>
                                            <span className="font-semibold block text-surface-400">Due Date:</span>
                                            <span>{generatedIssue.due_date || generatedIssue.dueDate}</span>
                                        </div>
                                    )}
                                    {(generatedIssue.estimation_time || generatedIssue.estimationTime) !== undefined && (generatedIssue.estimation_time || generatedIssue.estimationTime) !== null && (
                                        <div>
                                            <span className="font-semibold block text-surface-400">Estimation:</span>
                                            <span>{generatedIssue.estimation_time || generatedIssue.estimationTime} hours</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
